import React, { createRef, useContext, useState, useRef } from 'react'
import classNames from 'classnames'
import { useUID } from 'react-uid'

import { getGtmClient } from 'marketing-site/lib/gtm'
import { VALID_EMAIL } from 'marketing-site/lib/validEmail'
import { mq } from 'marketing-site/src/library/utils'
import { IInvalid, IValid } from 'marketing-site/src/library/elements/EmailForm'
import { CurrentUILocaleContext } from 'marketing-site/src/library/utils'
import { LoadingAnimation } from 'marketing-site/src/library/elements/LoadingAnimation'
import { Text } from 'marketing-site/src/library/elements/Text'
import { VisuallyHidden } from 'marketing-site/src/library/elements/VisuallyHidden'
import { useIOsInputScrollEvents } from 'marketing-site/src/library/hooks/useIosInputScrollEvents'
import { IProps } from './index'

export const EmailForm: React.FC<IProps> = ({
  size = 'large',
  buttonText,
  validity,
  placeholderText,
  value,
  small,
  onChange,
  onSubmit,
  inputRef: passedInputRef,
  loading,
  onFormFocus,
}: IProps) => {
  const { ui } = useContext(CurrentUILocaleContext)
  const selfRef = useRef<HTMLFormElement>(null)
  const { iosOnFocus, iosOnBlur } = useIOsInputScrollEvents()

  const componentId = useUID()
  const inputElementId = `email_${componentId}`
  const errorElementId = `signup-cta-error_${componentId}`

  const inputRef = passedInputRef || createRef<HTMLInputElement>()

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    onChange(event.target.value)
  }

  function handleFocus() {
    iosOnFocus()
    if (onFormFocus) {
      onFormFocus()
    }
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    onSubmit(event)
  }

  const classes = classNames('email-form', {
    'email-form--small': small,
    'email-form--loading': loading,
    'email-form--error': !validity.isValid,
  })

  const submitButtonClasses = classNames(
    'email-form__submit-button',
    ' email-form__submit-button--blue-fill',
    {
      'email-form__submit-button--small': size === 'small',
    },
  )

  return (
    <form ref={selfRef} className={classes} onSubmit={handleSubmit} noValidate>
      <VisuallyHidden>
        <label htmlFor={inputElementId}>
          {placeholderText || ui.localize('EmailForm.defaultEmailFieldPlaceholder')}
        </label>
      </VisuallyHidden>

      <div className="email-form__field">
        <input
          className="email-form__input"
          type="email"
          id={inputElementId}
          value={value}
          onFocus={handleFocus}
          onBlur={iosOnBlur}
          onChange={handleChange}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          aria-required="true"
          aria-invalid={!validity.isValid}
          aria-describedby={errorElementId}
          ref={inputRef}
          placeholder={placeholderText || ui.localize('EmailForm.defaultEmailFieldPlaceholder')}
          data-testid="email-form-input"
        />

        {!validity.isValid && (
          <div className="email-form__error-message" id={errorElementId} data-testid="error">
            <Text size="caption">{validity.errorText}</Text>
          </div>
        )}

        <button type="submit" className={submitButtonClasses} data-testid="submit">
          <Text size="body-bold">{buttonText}</Text>
          {loading && (
            <div className="email-form__submit__loading-indicator">
              <LoadingAnimation />
            </div>
          )}
        </button>
      </div>

      <style jsx>{`
        .email-form {
          width: 100%;
          max-width: 50rem;
          margin: 0 auto;
          position: relative;

          &--error {
            .email-form__input {
              box-shadow: inset 0 0 0 2px var(--ui-error);

              &:focus,
              &:focus-within {
                box-shadow: inset 0 0 0 2px var(--mvp-blue);
              }
            }
          }

          &__field {
            display: flex;
            flex-direction: column;
            align-content: center;
          }

          &__input {
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            height: ${size === 'large' ? '3rem' : '2.5rem'};
            padding: 0 1.125rem;
            border-radius: 1.5rem;
            background-color: var(--paper);
            box-shadow: inset 0 0 0 2px var(--mvp-slate);
            font: inherit;
            color: var(--mvp-slate);
            transition: box-shadow 0.24s;
            // Reset styles.
            appearance: none;
            border: none;
            outline: none;
            resize: none;

            &:focus,
            &:focus-within {
              box-shadow: inset 0 0 0 2px var(--mvp-blue);
            }

            &:disabled {
              opacity: 0.5;
              cursor: not-allowed;
            }

            &::placeholder {
              color: var(--neutral-500);
            }

            // Next query targets ios devices only
            @supports (-webkit-touch-callout: none) {
              font-size: 16px; // This prevents an undesired zoom in on Safari when the input is focused
            }
          }

          &__submit-button {
            @include mvp-button;
            width: 100%;
            margin-top: 0.5rem;

            &__loading-indicator {
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              width: 100%;
              height: 100%;
              position: absolute;
              top: 0;
              left: 0;
              border-radius: 2rem;
              background-color: inherit;
            }
          }

          &__error-message {
            display: inline-flex;
            padding: ${size === 'large' ? '0.5rem 1rem' : '0.25rem 0.75rem'};
            margin: 1rem auto 0;
            border-radius: 2rem;
            background-color: var(--ui-error);
            position: relative;
            text-align: center;

            :global(span) {
              color: var(--paper);
            }

            &:before {
              position: absolute;
              display: block;
              bottom: 100%;
              left: 50%;
              content: '';
              height: 10px;
              width: 10px;
              background-color: var(--ui-error);
              border-radius: 2px;
              transform: translate(-50%, 0) rotate(45deg);
              top: -4px;
            }
          }

          &--loading {
            .email-form__submit__label {
              opacity: 0;
            }
          }
        }

        @media (${mq.tablet}) {
          .email-form {
            flex-direction: row;

            &__field {
              display: grid;
              grid-template-columns: 1fr auto;
            }

            &__input {
              min-width: 13rem;
              border-top-right-radius: 0;
              border-bottom-right-radius: 0;
            }

            &__submit-button {
              margin-top: 0;
              border-top-left-radius: 0;
              border-bottom-left-radius: 0;
            }

            &__error-message {
              grid-row: 2;
            }
          }
        }
      `}</style>
    </form>
  )
}

export const useEmailValidation = ({
  allowPersonalEmails,
  invalidFormatErrorMessage,
  businessEmailErrorMessage,
}: {
  allowPersonalEmails: boolean
  invalidFormatErrorMessage: string
  businessEmailErrorMessage?: string
}) => {
  const [email, setEmail] = useState('')
  const [validity, setValidity] = useState<IValid | IInvalid>({ isValid: true })

  async function validate(): Promise<boolean> {
    if (!email || !VALID_EMAIL.test(email)) {
      setValidity({
        isValid: false,
        errorText: invalidFormatErrorMessage,
      })
      return false
    }

    const gtmClient = await getGtmClient()
    if (!allowPersonalEmails && (await gtmClient.personalEmailDomain(email))) {
      setValidity({
        isValid: false,
        errorText: businessEmailErrorMessage || 'Please enter a business email',
      })
      return false
    }

    return true
  }

  return { email, setEmail, validate, validity }
}
